import React, { ReactNode, useRef, useState } from 'react';
import useRefEffect from '@engoo/react-hooks/useRefEffect';
import keycode from 'keycode';
import { CallbackRef } from 'app/utils/react';

const isButtonKey = (e: KeyboardEvent) => {
  const key = keycode(e);

  return key === 'space' || key === 'enter';
};

type RefNode = HTMLElement | null;

export interface IFocusOutlineParams {
  showOutline: boolean;
  focusableRef: (elem: RefNode) => void;
}

/**
 * @deprecated Use useFocusOutlineState() instead
 */
export default function FocusOutlineState({ children }: {
  children: (params: IFocusOutlineParams) => ReactNode,
}) {
  const [showOutline, focusableRef] = useFocusOutlineState();

  return <>{children({ showOutline, focusableRef })}</>;
}

/**
 * This provides a `focusableRef` callback that can be passed as the `ref` to a DOM element. With this
 * ref, it keeps track of keyboard and mouse interaction and provides a `showOutline` boolean that will
 * be set to true when the element is focused with the keyboard, but not with the mouse. It also
 * makes the element keyboard-focuseable by setting its `tabIndex` to 0.
 *
 * This is intended to serve the use case of the new `:focus-visible` pseudo-selector,
 * which unfortunately is not supported in Safari yet.
 */
export function useFocusOutlineState(initialState: boolean = false): [
  boolean,
  CallbackRef,
] {
  const [showOutline, setShowOutline] = useState(initialState);
  const didMouseDownRef = useRef(false);

  const focusableRef: CallbackRef = useRefEffect((node: HTMLElement) => {
    const onFocus = () => {
      // Don't show outline if the user focuses the element by clicking.
      if (!didMouseDownRef.current) {
        setShowOutline(true);
      }
    };

    const onBlur = () => setShowOutline(false);

    const onMouseDown = () => {
      didMouseDownRef.current = true;
      setTimeout(() => { didMouseDownRef.current = false; });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (isButtonKey(e)) {
        setShowOutline(true);
      }
    };

    if (!node.getAttribute('tabIndex')) {
      // Only set if it has not been set yet
      node.setAttribute('tabIndex', '0');
    }
    node.addEventListener('mousedown', onMouseDown);
    node.addEventListener('keydown', onKeyDown);
    node.addEventListener('focus', onFocus);
    node.addEventListener('blur', onBlur);

    return () => {
      node.removeEventListener('mousedown', onMouseDown);
      node.removeEventListener('keydown', onKeyDown);
      node.removeEventListener('focus', onFocus);
      node.removeEventListener('blur', onBlur);
    };
  }, []);

  return [showOutline, focusableRef];
}
